import { Alert, Button, Card, Group, List, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useI18n } from '../i18n/useT.js';
import { PeriodPicker, toQuery } from '../components/PeriodPicker.js';
import type { Period } from '../components/PeriodPicker.js';
import { useGenerateSaft } from '../api/hooks.js';

/**
 * SAF-T — the file the Danish Tax Authority can demand on request.
 *
 * The refusals are the useful part of this feature, not an obstacle to it. The
 * export declines rather than guesses in two cases, and each names something a
 * person can fix:
 *
 * - a **VAT code longer than nine characters**, which the schema's `SAFcodeType`
 *   cannot carry — nine of the official codes are;
 * - a **company without a CVR, address or bank account**, all three of which the
 *   schema requires in the header. A placeholder would produce a file that
 *   validates and misstates who filed it.
 *
 * So this page shows the API's own message rather than "export failed", and
 * says up front what the export needs.
 */

/** What the browser is handed once the XML arrives. */
interface Generated {
  readonly xml: string;
  readonly filename: string;
}

/**
 * The current calendar year, as a starting point.
 *
 * A SAF-T file is asked for over a period, and that period is almost always a
 * financial year. Opening on two empty fields makes the common case work for
 * something, and leaves the page in a state where the only button is disabled
 * and nothing says why until you read the small print. Both ends stay editable.
 */
function currentYear(): Period {
  const year = new Date().getFullYear();
  return { from: new Date(year, 0, 1), to: new Date(year, 11, 31) };
}

export function SaftPage(): ReactNode {
  const { t, locale } = useI18n();
  const generate = useGenerateSaft();
  const [period, setPeriod] = useState<Period>(currentYear);
  const [result, setResult] = useState<Generated | null>(null);

  const query = toQuery(period);
  const complete = query.from !== undefined && query.to !== undefined;

  function handleGenerate(): void {
    if (query.from === undefined || query.to === undefined) {
      return;
    }
    setResult(null);
    generate.mutate(
      { from: query.from, to: query.to },
      {
        onSuccess: (xml) => {
          setResult({ xml, filename: `saft-${query.from ?? ''}-${query.to ?? ''}.xml` });
          notifications.show({
            color: 'green',
            title: t('saft.generated'),
            message: t('saft.generatedDetail'),
          });
        },
      },
    );
  }

  /**
   * Hand the file to the browser.
   *
   * Not a plain link to the endpoint: it needs the company access token, which
   * a link cannot carry. The XML is already in memory from the request, so it
   * is wrapped in a blob and downloaded from there.
   */
  function handleDownload(): void {
    if (result === null) {
      return;
    }
    const blob = new Blob([result.xml], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = result.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Released once the download has been handed over; the object would
    // otherwise be held for the lifetime of the document.
    URL.revokeObjectURL(url);
  }

  return (
    <Stack gap="lg">
      <Title order={2}>{t('saft.title')}</Title>

      <Alert color="gray" variant="light" title={t('saft.whatTitle')}>
        {t('saft.what')}
      </Alert>

      <Card withBorder padding="md">
        <Stack gap="md">
          <Text fw={500}>{t('saft.choosePeriod')}</Text>
          <Text size="sm" c="dimmed">
            {t('saft.bothInclusive')}
          </Text>

          <PeriodPicker value={period} onChange={setPeriod} />

          <Group>
            <Button
              onClick={handleGenerate}
              loading={generate.isPending}
              disabled={!complete}
            >
              {t('saft.generate')}
            </Button>
            {result !== null && (
              <Button variant="light" onClick={handleDownload}>
                {t('saft.download', { filename: result.filename })}
              </Button>
            )}
          </Group>

          {!complete && (
            <Text size="sm" c="dimmed">
              {t('saft.datesRequired')}
            </Text>
          )}
        </Stack>
      </Card>

      {/*
        The API's own words, with the request id. Each refusal names something
        to fix — a VAT code the schema cannot carry, or a missing CVR, adresse
        or bankkonto — and replacing that with "eksporten mislykkedes" would
        throw away the only part that tells anyone what to do.
      */}
      {generate.isError && (
        <ErrorMessage error={generate.error} title={t('saft.failed')} />
      )}

      {generate.isError && (
        <Alert color="blue" variant="light" title={t('saft.requiresTitle')}>
          <List size="sm" spacing={4}>
            <List.Item>{t('saft.requires.company')}</List.Item>
            <List.Item>{t('saft.requires.vatCodes')}</List.Item>
            <List.Item>{t('saft.requires.contacts')}</List.Item>
          </List>
        </Alert>
      )}

      {result !== null && (
        <Alert color="green" variant="light" title={t('saft.doneTitle')}>
          <Text size="sm">
            {t('saft.doneDetail', {
              filename: result.filename,
              // Grouping separators follow the language: 1.234.567 in Danish,
              // 1,234,567 in English. A hard-coded 'da-DK' would print Danish
              // separators inside an English sentence.
              characters: new Intl.NumberFormat(locale === 'da' ? 'da-DK' : 'en-GB').format(
                result.xml.length,
              ),
            })}
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
